import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useLanguage } from '../../../contexts/LanguageContext';

const CriticalAlertsPanel = ({ alerts, onResolveAlert }) => {
  const { t, isRTL } = useLanguage();

  const alertTypeConfig = {
    expiring_soon: {
      icon: 'Clock',
      color: 'text-warning',
      bgColor: 'bg-warning/10',
      borderColor: 'border-warning/20',
      label: t('visas.expirationNear')
    },
    missing_documents: {
      icon: 'FileWarning',
      color: 'text-destructive',
      bgColor: 'bg-destructive/10',
      borderColor: 'border-destructive/20',
      label: t('visas.missingDocuments')
    },
    processing_delay: {
      icon: 'AlertTriangle',
      color: 'text-error',
      bgColor: 'bg-error/10',
      borderColor: 'border-error/20',
      label: t('visas.processingDelay')
    },
    compliance_issue: {
      icon: 'ShieldAlert',
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      borderColor: 'border-primary/20',
      label: t('visas.complianceIssue')
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-3 sm:p-4 md:p-6 shadow-warm">
      <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
        <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-error/10 flex items-center justify-center flex-shrink-0">
          <Icon name="AlertCircle" size={20} className="text-error" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-base sm:text-lg font-semibold text-foreground truncate">{t('visas.criticalAlertsTitle')}</h3>
          <p className="caption text-muted-foreground truncate">
            {alerts?.length} {alerts?.length !== 1 ? t('visas.alertsPlural') : t('visas.alertsSingular')} {t('visas.immediateAttention')}
          </p>
        </div>
      </div>
      <div className="space-y-3">
        {alerts?.map((alert) => {
          const config = alertTypeConfig?.[alert?.type] || alertTypeConfig?.processing_delay;
          
          return (
            <div 
              key={alert?.id} 
              className={`p-3 sm:p-4 rounded-lg border ${config?.bgColor} ${config?.borderColor}`}
            >
              <div className="flex items-start gap-2 sm:gap-3">
                <Icon name={config?.icon} size={20} className={`${config?.color} flex-shrink-0`} />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 sm:gap-4 mb-2">
                    <div className="min-w-0 flex-1">
                      <p className={`font-medium ${config?.color} mb-1 truncate`}>{config?.label}</p>
                      <p className="text-sm text-foreground font-medium truncate">{alert?.pilgrimName}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Check"
                      onClick={() => onResolveAlert(alert?.id)}
                      className="self-start sm:self-auto"
                    >
                      <span className="hidden sm:inline">{t('visas.resolve')}</span>
                      <span className="sm:hidden">{t('visas.resolve')}</span>
                    </Button>
                  </div>
                  <p className="text-xs sm:text-sm text-muted-foreground mb-2 break-words">{alert?.message}</p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Icon name="Clock" size={12} className="flex-shrink-0" />
                    <span className="truncate">{new Date(alert.timestamp)?.toLocaleString('en-US')}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {alerts?.length === 0 && (
          <div className="text-center py-6 sm:py-8">
            <Icon name="CheckCircle" size={48} className="text-success mx-auto mb-3" />
            <p className="text-muted-foreground text-sm sm:text-base">{t('visas.noCriticalAlerts')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CriticalAlertsPanel;